/*!
 * stereo-check.js — let a stereo result prove itself to the person holding it.
 *
 * Why this exists. A tool that places a mono signal in one ear produces a file
 * whose whole meaning is "which channel". Until 2026-09-17 /mono-to-stereo
 * could not show that: it handed over a download and nothing else. When a
 * listener's own playback chain folds the two channels together — a mono
 * headset, one speaker, Windows' "mono audio" switch, a half-inserted plug —
 * a correctly panned file sounds wrong, and neither the listener nor we can
 * tell that from the file. That cost a real week: report #10000851 (pt),
 * two engineering investigations, five emails, and a paying user who still
 * could not tell whether the fault was ours or his cable's.
 *
 * So this module answers the question in two ways that do NOT depend on the
 * listener's speakers:
 *
 *   • METERS — live per-channel level read off the decoded output. A silent
 *     right channel reads silent on screen even if the listener hears it in
 *     both ears. This is the proof; the ears are the symptom.
 *   • ISOLATION — "left only" plays the LEFT channel into BOTH outputs, so a
 *     listener on any chain, mono or stereo, hears what that channel holds.
 *     Together the two buttons answer "is the sound where I asked for it?"
 *     without a working stereo path at all.
 *
 * And one more, for the chain itself:
 *
 *   • TONE TEST — a hard-panned tone generated here, so the page can say
 *     "if you hear the left tone on both sides, your chain is mixing the
 *     channels" without asking anyone to go find the Windows sound panel.
 *
 * API
 *   var v = StereoCheck.create(container, { strings, onEvent });
 *   v.load(blob, { verdict: 'left'|'right'|'same'|'wide' });   // show + arm
 *   v.stop();  v.destroy();
 *
 * `strings` carries the page's own translations; this module ships no copy.
 */
(function () {
    'use strict';

    var SILENT_DB = -80;          // below this a channel is "silence" for a human
    var METER_FLOOR_DB = -60;     // bottom of the drawn scale

    function dbOf(peak) { return peak > 0 ? 20 * Math.log10(peak) : -Infinity; }

    function el(tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        return n;
    }

    /* The module carries its own styles, the way mic-monitor.js does: a shared
       control whose CSS lives in one consumer's blade is a control the second
       consumer gets half a control. style-src allows inline, so no nonce is needed. */
    var styled = false;
    function ensureStyle() {
        if (styled) return;
        styled = true;
        var s = document.createElement('style');
        s.textContent = [
            '.sck{margin:18px 0 4px;text-align:start}',
            '.sck-head{display:flex;align-items:center;gap:12px;margin-bottom:12px}',
            '.sck-play{display:inline-flex;align-items:center;gap:8px;min-height:40px;padding:9px 22px;border:1px solid var(--border);border-radius:999px;background:var(--bg-tertiary);color:var(--text-primary);font:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:border-color .15s,background .15s}',
            '.sck-play:hover{border-color:var(--accent);color:var(--accent)}',
            '.sck-time{font-size:13px;color:var(--text-muted);font-variant-numeric:tabular-nums}',
            '.sck-monitor{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px}',
            '.sck-mode{padding:7px 16px;border:1px solid var(--border);border-radius:20px;background:transparent;color:var(--text-secondary);font:inherit;font-size:13px;font-weight:600;cursor:pointer;transition:.2s}',
            '.sck-mode:hover{border-color:var(--accent);color:var(--accent)}',
            '.sck-mode.on{border-color:var(--accent);background:rgba(56,189,248,.1);color:var(--accent)}',
            '.sck-hint{font-size:13px;color:var(--text-muted);line-height:1.6;margin-bottom:12px}',
            '.sck-meters{display:flex;flex-direction:column;gap:6px;margin-bottom:10px}',
            '.sck-row{display:flex;align-items:center;gap:10px}',
            '.sck-ch{font-size:13px;font-weight:700;color:var(--text-secondary);min-width:2.5em}',
            '.sck-bar{flex:1;height:10px;border-radius:5px;background:var(--border);overflow:hidden}',
            '.sck-bar>i{display:block;height:100%;width:0;border-radius:5px;background:var(--accent-gradient);transition:width .06s linear}',
            '.sck-db{font-size:13px;color:var(--text-muted);min-width:5.5em;text-align:end;font-variant-numeric:tabular-nums}',
            '.sck-verdict{font-size:14px;line-height:1.6;color:var(--text-secondary);margin-bottom:10px}',
            '.sck-verdict-pan{color:var(--text-primary);font-weight:600}',
            '.sck-chain{font-size:13px}',
            '.sck-chain>summary{cursor:pointer;color:var(--text-muted);padding:6px 0}',
            '.sck-chain>summary:hover{color:var(--accent)}',
            '.sck-chain-body p{margin:6px 0 10px;color:var(--text-secondary);line-height:1.6}',
            '.sck-tones{display:flex;gap:8px;flex-wrap:wrap}',
            '.sck-tone{padding:7px 16px;border:1px solid var(--border);border-radius:20px;background:transparent;color:var(--text-secondary);font:inherit;font-size:13px;cursor:pointer}',
            '.sck-tone:hover{border-color:var(--accent);color:var(--accent)}',
            '.sck-tone-said{min-height:1.3em;margin-top:8px;color:var(--accent);font-size:13px}',
        ].join('');
        document.head.appendChild(s);
    }

    function create(container, opts) {
        ensureStyle();
        opts = opts || {};
        var S = opts.strings || {};
        var emit = typeof opts.onEvent === 'function' ? opts.onEvent : function () {};

        var audio = null, url = null, ctx = null, srcNode = null;
        var splitter = null, gL = null, gR = null, merger = null, anL = null, anR = null;
        var raf = 0, mode = 'both', maxL = 0, maxR = 0, measuredMs = 0, lastT = 0, reported = false;
        var toneStop = null;

        /* ── DOM ──────────────────────────────────────────────────────── */
        var root = el('div', 'sck');
        root.innerHTML = '';

        var head = el('div', 'sck-head');
        var playBtn = el('button', 'sck-play');
        playBtn.type = 'button';
        var playLabel = el('span', null, S.play || 'Play');
        playBtn.appendChild(playLabel);
        var timeEl = el('span', 'sck-time', '0:00');
        head.appendChild(playBtn);
        head.appendChild(timeEl);

        var monitor = el('div', 'sck-monitor');
        var MODES = [['both', S.both || 'Both'], ['left', S.left_only || 'Left only'], ['right', S.right_only || 'Right only']];
        var modeBtns = {};
        MODES.forEach(function (m) {
            var b = el('button', 'sck-mode' + (m[0] === 'both' ? ' on' : ''), m[1]);
            b.type = 'button';
            b.addEventListener('click', function () { setMode(m[0]); });
            modeBtns[m[0]] = b;
            monitor.appendChild(b);
        });
        var monitorHint = el('div', 'sck-hint', S.isolation_hint || '');

        function meterRow(name) {
            var row = el('div', 'sck-row');
            row.appendChild(el('span', 'sck-ch', name));
            var bar = el('div', 'sck-bar');
            var fill = el('i');
            bar.appendChild(fill);
            row.appendChild(bar);
            var val = el('span', 'sck-db', '—');
            row.appendChild(val);
            return { row: row, fill: fill, val: val };
        }
        var mL = meterRow(S.ch_left || 'L');
        var mR = meterRow(S.ch_right || 'R');
        var meters = el('div', 'sck-meters');
        meters.appendChild(mL.row);
        meters.appendChild(mR.row);

        var verdictEl = el('div', 'sck-verdict');

        /* The chain test. Folded away by default: it is the answer to a
           question most people never ask, and a permanent fixture would make
           every correct result look doubtful. */
        var chain = el('details', 'sck-chain');
        var sum = el('summary', null, S.chain_test || 'Hear it in both ears? Test your headphones');
        chain.appendChild(sum);
        var chainBody = el('div', 'sck-chain-body');
        chainBody.appendChild(el('p', null, S.chain_hint || ''));
        var toneRow = el('div', 'sck-tones');
        var toneL = el('button', 'sck-tone', S.tone_left || 'Tone: left');
        var toneR = el('button', 'sck-tone', S.tone_right || 'Tone: right');
        toneL.type = toneR.type = 'button';
        toneRow.appendChild(toneL);
        toneRow.appendChild(toneR);
        chainBody.appendChild(toneRow);
        var toneSaid = el('div', 'sck-tone-said');
        chainBody.appendChild(toneSaid);
        chain.appendChild(chainBody);

        root.appendChild(head);
        root.appendChild(monitor);
        if (S.isolation_hint) root.appendChild(monitorHint);
        root.appendChild(meters);
        root.appendChild(verdictEl);
        root.appendChild(chain);
        container.appendChild(root);

        /* ── graph ────────────────────────────────────────────────────── */
        async function ensureCtx() {
            if (!ctx) {
                var AC = window.AudioContext || window.webkitAudioContext;
                if (!AC) throw new Error('no-webaudio');
                ctx = new AC();
            }
            /* The shared door: a context whose output device was taken away
               never comes back from resume(), and the page would sit on a
               "pause" button over silence. */
            if (window.__liveAudioCtxReady) {
                var live = await window.__liveAudioCtxReady(ctx, function () {
                    var AC2 = window.AudioContext || window.webkitAudioContext;
                    return new AC2();
                });
                if (live && live !== ctx) {
                    /* A media element can be the source of exactly ONE
                       MediaElementAudioSourceNode for its whole life — a second
                       createMediaElementSource throws, even on a different
                       context. So when the old context dies (its output device
                       was taken away) the element has to be replaced with it,
                       or the recovery leaves a Play button over silence. */
                    ctx = live;
                    teardownGraph();
                    remakeAudio();
                }
            } else if (ctx.state === 'suspended') {
                try { await ctx.resume(); } catch (_) {}
            }
            return ctx;
        }

        function makeAudio() {
            var a = new Audio();
            a.src = url;
            a.preload = 'metadata';
            a.addEventListener('play', onPlay);
            a.addEventListener('pause', onPause);
            a.addEventListener('ended', onPause);
            return a;
        }
        function remakeAudio() {
            if (!url) return;
            var t = audio ? audio.currentTime : 0;
            try { audio.pause(); } catch (_) {}
            audio = makeAudio();
            try { audio.currentTime = t; } catch (_) {}
        }

        function teardownGraph() {
            [srcNode, splitter, gL, gR, merger, anL, anR].forEach(function (n) {
                if (n) { try { n.disconnect(); } catch (_) {} }
            });
            srcNode = splitter = gL = gR = merger = anL = anR = null;
        }

        function buildGraph() {
            if (srcNode) return;
            srcNode = ctx.createMediaElementSource(audio);
            splitter = ctx.createChannelSplitter(2);
            merger = ctx.createChannelMerger(2);
            gL = ctx.createGain();
            gR = ctx.createGain();
            anL = ctx.createAnalyser(); anL.fftSize = 2048;
            anR = ctx.createAnalyser(); anR.fftSize = 2048;
            srcNode.connect(splitter);
            /* Analysers sit on the SPLITTER, before the monitor routing, so the
               numbers keep describing the file no matter which button is down. */
            splitter.connect(anL, 0);
            splitter.connect(anR, 1);
            splitter.connect(gL, 0);
            splitter.connect(gR, 1);
            merger.connect(ctx.destination);
            wireMode();
        }

        function wireMode() {
            try { gL.disconnect(); } catch (_) {}
            try { gR.disconnect(); } catch (_) {}
            if (mode === 'both') {
                gL.connect(merger, 0, 0);
                gR.connect(merger, 0, 1);
            } else if (mode === 'left') {
                // Left channel into BOTH outputs — audible on a mono chain too.
                gL.connect(merger, 0, 0);
                gL.connect(merger, 0, 1);
            } else {
                gR.connect(merger, 0, 0);
                gR.connect(merger, 0, 1);
            }
        }

        function setMode(m) {
            mode = m;
            Object.keys(modeBtns).forEach(function (k) { modeBtns[k].classList.toggle('on', k === m); });
            if (merger) wireMode();
            emit('monitor_mode', { mode: m });
        }

        /* ── meters ───────────────────────────────────────────────────── */
        var bufL = null, bufR = null;
        function peakOf(an, buf) {
            an.getFloatTimeDomainData(buf);
            var p = 0;
            for (var i = 0; i < buf.length; i++) { var v = buf[i] < 0 ? -buf[i] : buf[i]; if (v > p) p = v; }
            return p;
        }
        function paint(fill, val, peak, max) {
            var db = dbOf(peak);
            var pct = db <= METER_FLOOR_DB ? 0 : Math.min(100, (db - METER_FLOOR_DB) / -METER_FLOOR_DB * 100);
            fill.style.width = pct.toFixed(1) + '%';
            var mdb = dbOf(max);
            val.textContent = mdb === -Infinity || mdb < SILENT_DB
                ? (S.silent || 'silent')
                : Math.round(mdb) + ' dB';
        }
        function tick() {
            raf = requestAnimationFrame(tick);
            if (!anL) return;
            if (!bufL) { bufL = new Float32Array(anL.fftSize); bufR = new Float32Array(anR.fftSize); }
            var pL = peakOf(anL, bufL), pR = peakOf(anR, bufR);
            if (pL > maxL) maxL = pL;
            if (pR > maxR) maxR = pR;
            paint(mL.fill, mL.val, pL, maxL);
            paint(mR.fill, mR.val, pR, maxR);
            var now = performance.now();
            if (lastT) measuredMs += now - lastT;
            lastT = now;
            if (audio) timeEl.textContent = fmt(audio.currentTime) + ' / ' + fmt(audio.duration || 0);
        }
        function fmt(s) {
            if (!isFinite(s) || s < 0) s = 0;
            var m = Math.floor(s / 60), x = Math.floor(s % 60);
            return m + ':' + (x < 10 ? '0' : '') + x;
        }

        /* ── tone test ────────────────────────────────────────────────── */
        async function tone(side) {
            if (toneStop) toneStop();
            await ensureCtx();
            var osc = ctx.createOscillator();
            var g = ctx.createGain();
            var mrg = ctx.createChannelMerger(2);
            osc.type = 'sine';
            osc.frequency.value = 440;
            g.gain.value = 0.0001;
            osc.connect(g);
            g.connect(mrg, 0, side === 'left' ? 0 : 1);
            mrg.connect(ctx.destination);
            var t0 = ctx.currentTime;
            g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.05);
            g.gain.setValueAtTime(0.18, t0 + 1.0);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.2);
            osc.start(t0);
            osc.stop(t0 + 1.25);
            toneSaid.textContent = (side === 'left' ? (S.tone_playing_left || '') : (S.tone_playing_right || ''));
            toneStop = function () { try { osc.stop(); } catch (_) {} try { mrg.disconnect(); } catch (_) {} toneStop = null; };
            osc.onended = function () { toneStop = null; };
            emit('chain_tone', { side: side });
        }
        toneL.addEventListener('click', function () { tone('left'); });
        toneR.addEventListener('click', function () { tone('right'); });

        /* ── play ─────────────────────────────────────────────────────── */
        playBtn.addEventListener('click', async function () {
            if (!audio) return;
            if (!audio.paused) { audio.pause(); return; }
            try {
                await ensureCtx();
                buildGraph();
                await audio.play();
            } catch (e) {
                emit('play_failed', { err: String((e && e.name) || e).slice(0, 40) });
                if (window.__toast && S.play_failed) window.__toast(S.play_failed, 'error');
            }
        });

        function onPlay() {
            playLabel.textContent = S.pause || 'Pause';
            lastT = 0;
            if (!raf) raf = requestAnimationFrame(tick);
        }
        function onPause() {
            playLabel.textContent = S.play || 'Play';
            lastT = 0;
            if (raf) { cancelAnimationFrame(raf); raf = 0; }
            /* Someone listened. Say what the two channels measured, so "is the
               panning right for anyone else?" becomes a query instead of an
               investigation — the question that cost this tool a week. */
            if (measuredMs > 700 && !reported) {
                reported = true;
                var l = dbOf(maxL), r = dbOf(maxR);
                emit('measured', {
                    l_db: l === -Infinity ? -999 : Math.round(l),
                    r_db: r === -Infinity ? -999 : Math.round(r),
                    ms: Math.round(measuredMs), mode: mode,
                });
            }
        }

        return {
            root: root,

            /**
             * Show the produced file. `verdict` is what the tool BUILT, so the
             * page can state the answer before a single frame is played; the
             * meters then confirm it from the decoded bytes.
             */
            load: function (blob, info) {
                this.stop();
                info = info || {};
                if (url) { try { URL.revokeObjectURL(url); } catch (_) {} }
                url = URL.createObjectURL(blob);
                audio = makeAudio();
                maxL = maxR = 0; measuredMs = 0; reported = false;
                mL.val.textContent = mR.val.textContent = '—';
                mL.fill.style.width = mR.fill.style.width = '0%';
                teardownGraph();
                setMode('both');
                var key = 'verdict_' + (info.verdict || 'same');
                verdictEl.textContent = S[key] || '';
                verdictEl.className = 'sck-verdict' + (info.verdict === 'left' || info.verdict === 'right' ? ' sck-verdict-pan' : '');
                toneSaid.textContent = '';
                root.hidden = false;
                emit('verify_shown', { verdict: info.verdict || 'same' });
            },

            /** Measured peaks so far, for the tool's own telemetry. */
            measured: function () {
                return { l_db: Math.round(dbOf(maxL)), r_db: Math.round(dbOf(maxR)), ms: Math.round(measuredMs) };
            },

            stop: function () {
                if (audio) { try { audio.pause(); } catch (_) {} }
                if (toneStop) toneStop();
                onPause();
            },

            destroy: function () {
                this.stop();
                teardownGraph();
                if (url) { try { URL.revokeObjectURL(url); } catch (_) {} url = null; }
                audio = null;
                if (root.parentNode) root.parentNode.removeChild(root);
            },
        };
    }

    window.StereoCheck = { create: create, SILENT_DB: SILENT_DB };
})();
